import { Link, useNavigate } from "@tanstack/react-router";
import { useQuery } from "@tanstack/react-query";
import { format } from "date-fns";
import {
  Activity,
  CalendarPlus,
  CalendarX,
  Clock,
  UserPlus,
  Users,
} from "lucide-react";
import {
  APPOINTMENT_STATUS_LABELS,
  APPOINTMENT_TYPE_LABELS,
  type AuditLogEntry,
} from "@clinic/types";
import { appointmentStatusTone } from "@clinic/ui";
import { api } from "@/lib/api";
import { PageHeader } from "@/components/PageHeader";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Card, CardTitle } from "@/components/ui/card";
import { Skeleton, SkeletonRows } from "@/components/ui/skeleton";

function StatCard({
  icon: Icon,
  label,
  value,
  hint,
}: {
  icon: typeof Users;
  label: string;
  value: string | number;
  hint?: string;
}) {
  return (
    <Card className="flex items-center gap-4">
      <div className="flex h-11 w-11 items-center justify-center rounded-lg bg-accent-subtle/40 text-accent-light">
        <Icon size={20} />
      </div>
      <div>
        <div className="text-xl font-semibold">{value}</div>
        <div className="text-xs text-text-secondary">{label}</div>
        {hint && <div className="text-xs text-text-tertiary">{hint}</div>}
      </div>
    </Card>
  );
}

function activityLine(entry: AuditLogEntry): string {
  const who = entry.user?.name ?? "Someone";
  const action = entry.action.toLowerCase();
  return `${who} ${action === "login" ? "logged in" : `${action}d ${entry.entity.toLowerCase()}`}`;
}

export function DashboardPage() {
  const navigate = useNavigate();
  const stats = useQuery({ queryKey: ["dashboard"], queryFn: () => api.dashboard.stats() });

  if (stats.isLoading) {
    return (
      <div>
        <PageHeader title="Dashboard" />
        <div className="mb-6 grid grid-cols-2 gap-4 lg:grid-cols-4">
          {Array.from({ length: 4 }).map((_, i) => (
            <Skeleton key={i} className="h-24" />
          ))}
        </div>
        <SkeletonRows rows={4} />
      </div>
    );
  }

  const data = stats.data;
  if (!data) return null;
  const maxDay = Math.max(1, ...data.appointmentsByDay.map((d) => d.count));

  return (
    <div>
      <PageHeader
        title="Dashboard"
        subtitle={format(new Date(), "EEEE, d MMMM yyyy")}
        actions={
          <>
            <Button variant="ghost" onClick={() => navigate({ to: "/patients" })}>
              <UserPlus size={15} /> New patient
            </Button>
            <Button onClick={() => navigate({ to: "/appointments" })}>
              <CalendarPlus size={15} /> New appointment
            </Button>
          </>
        }
      />

      <div className="mb-6 grid grid-cols-2 gap-4 lg:grid-cols-4">
        <StatCard icon={Clock} label="Appointments today" value={data.todayCount} />
        <StatCard icon={CalendarPlus} label="Appointments this week" value={data.weekCount} />
        <StatCard
          icon={Users}
          label="Active patients"
          value={data.totalPatients}
          hint={`+${data.newPatientsThisMonth} this month`}
        />
        <StatCard
          icon={CalendarX}
          label="Cancellation rate (30d)"
          value={`${Math.round(data.cancellationRate * 100)}%`}
        />
      </div>

      <div className="grid gap-6 xl:grid-cols-3">
        {/* today's schedule */}
        <Card className="xl:col-span-2">
          <CardTitle>Today's schedule</CardTitle>
          {data.todayAppointments.length === 0 ? (
            <p className="py-8 text-center text-sm text-text-tertiary">
              No appointments scheduled for today.
            </p>
          ) : (
            <div className="space-y-2">
              {data.todayAppointments.map((a) => (
                <Link
                  key={a.id}
                  to="/patients/$patientId"
                  params={{ patientId: a.patientId }}
                  className="flex items-center gap-4 rounded-lg border bg-bg px-4 py-3 transition hover:bg-bg-tertiary"
                  style={{ borderColor: "var(--accent-subtle)" }}
                >
                  <span className="font-mono text-sm text-accent-light">
                    {format(new Date(a.startTime), "HH:mm")}
                  </span>
                  <div className="min-w-0 flex-1">
                    <div className="truncate text-sm font-medium">{a.patient?.fullName}</div>
                    <div className="text-xs text-text-tertiary">
                      {APPOINTMENT_TYPE_LABELS[a.type]} · {a.doctor?.name}
                    </div>
                  </div>
                  <Badge tone={appointmentStatusTone[a.status] ?? "neutral"}>
                    {APPOINTMENT_STATUS_LABELS[a.status]}
                  </Badge>
                </Link>
              ))}
            </div>
          )}

          {/* weekly mini bar chart */}
          <div className="mt-6 border-t pt-4" style={{ borderColor: "var(--accent-subtle)" }}>
            <div className="mb-3 text-xs font-semibold uppercase tracking-wide text-text-tertiary">
              This week
            </div>
            <div className="flex items-end gap-2" style={{ height: 72 }}>
              {data.appointmentsByDay.map((d) => (
                <div key={d.date} className="flex flex-1 flex-col items-center gap-1">
                  <span className="text-xs text-text-secondary">{d.count || ""}</span>
                  <div
                    className="w-full rounded-t bg-accent transition-all"
                    style={{ height: `${(d.count / maxDay) * 48 + 2}px`, opacity: d.count ? 1 : 0.25 }}
                  />
                  <span className="text-xs text-text-tertiary">
                    {format(new Date(d.date), "EEE")}
                  </span>
                </div>
              ))}
            </div>
          </div>
        </Card>

        {/* recent activity */}
        <Card>
          <CardTitle className="flex items-center gap-2">
            <Activity size={16} className="text-accent-light" /> Recent activity
          </CardTitle>
          {data.recentActivity.length === 0 ? (
            <p className="py-8 text-center text-sm text-text-tertiary">No activity yet.</p>
          ) : (
            <ul className="space-y-3">
              {data.recentActivity.map((entry) => (
                <li key={entry.id} className="flex items-start gap-3 text-sm">
                  <span className="mt-1.5 h-1.5 w-1.5 shrink-0 rounded-pill bg-accent" />
                  <div>
                    <span>{activityLine(entry)}</span>
                    <div className="text-xs text-text-tertiary">
                      {format(new Date(entry.createdAt), "d MMM, HH:mm")}
                    </div>
                  </div>
                </li>
              ))}
            </ul>
          )}
        </Card>
      </div>
    </div>
  );
}
